<template>
  <div class="game-page">
    <div class="game-shell">
      <section class="game-overview" v-if="item.id && !start">
        <div class="overview-copy">
          <div class="overview-glow" aria-hidden="true"></div>
          <div class="crumbs">
            <router-link to="/">Home</router-link>
            <span>/</span>
            <span>{{ item.name }}</span>
          </div>
          <span class="status-tag">{{ item.flag || "featured" }}</span>
          <h1>{{ item.name }}</h1>
          <p class="overview-desc">{{ cleanDesc(item.desc) }}</p>

          <div class="overview-meta">
            <div>
              <span class="meta-icon">
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 21 3.5 16.2V7.8L12 3l8.5 4.8v8.4Z"/></svg>
              </span>
              <strong>{{ item.votes }}</strong>
              <span>Player votes</span>
            </div>
            <div>
              <span class="meta-icon">
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 4h14v16H5Zm3 3v10h8V7Z"/></svg>
              </span>
              <strong>HTML5</strong>
              <span>Instant start</span>
            </div>
            <div>
              <span class="meta-icon">
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5"/></svg>
              </span>
              <strong>Full screen</strong>
              <span>Immersive mode</span>
            </div>
          </div>

          <div class="play-launch">
            <button class="launch-button" @click="onStart" :disabled="!preloadReady">
              <span>Start Game</span>
              <em>{{ btnText }}</em>
            </button>
            <div class="launch-progress">
              <span class="launch-bar" :style="{ width: progress + '%' }"></span>
            </div>
          </div>

          <div class="store-links" v-if="item.android || item.ios">
            <a v-if="item.android" :href="item.android" target="_blank" rel="noopener noreferrer">
              <img src="/static/assets/images/google-play-badge.png" alt="Google Play" />
            </a>
            <a v-if="item.ios" :href="item.ios" target="_blank" rel="noopener noreferrer">
              <img src="/static/assets/images/app-store-badge.png" alt="App Store" />
            </a>
          </div>
        </div>

        <div class="overview-art">
          <img :src="item.image" :alt="item.name" />
          <div class="overview-art-overlay">
            <span>Featured Arcade</span>
            <strong>{{ item.name }}</strong>
          </div>
        </div>
      </section>

      <section class="game-stage" v-if="item.id && start">
        <div class="stage-topbar">
          <div>
            <span class="stage-kicker">Now playing</span>
            <h2>{{ item.name }}</h2>
          </div>
          <div class="stage-actions">
            <button class="stage-fullscreen" @click="toggleFullscreen">
              <span class="button-icon">
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5"/></svg>
              </span>
              {{ isFullscreen ? "Exit Fullscreen" : "Fullscreen" }}
            </button>
            <router-link class="stage-back" to="/">
              <span class="button-icon">
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M10 7 5 12l5 5M6 12h13"/></svg>
              </span>
              Return to Library
            </router-link>
          </div>
        </div>

        <div class="stage-layout">
          <game-left :item="item" />

          <div class="stage-frame-shell" :style="areaCss">
            <div class="frame-aura" aria-hidden="true"></div>
            <div class="frame-loading" v-if="!frameReady">
              <img class="frame-loading-art" :src="item.image" :alt="item.name" />
              <div class="frame-loading-copy">
                <span class="frame-loading-kicker">Launching</span>
                <strong>{{ item.name }}</strong>
                <p>{{ frameStatusText }}</p>
                <div class="frame-loading-progress" aria-hidden="true">
                  <span class="frame-loading-progress-bar" :style="{ width: progress + '%' }"></span>
                </div>
                <span class="frame-loading-progress-text">{{ Math.round(progress) }}%</span>
              </div>
            </div>
            <iframe
              ref="gameFrame"
              id="gameFrame"
              :src="gameUrl"
              :style="frameCss"
              :class="{ 'is-ready': frameReady }"
              frameborder="0"
              scrolling="no"
              loading="eager"
              allowfullscreen
              @load="onFrameLoad"
            ></iframe>
          </div>

          <game-right :games="relatedGames" />
        </div>
      </section>
    </div>
  </div>
</template>

<script>
const PORTRAIT_RATIO = 9 / 16
const LANDSCAPE_RATIO = 16 / 9
import GameLeft from "../components/GameLeft.vue"
import GameRight from "../components/GameRight.vue"
import { cleanDesc, getGameById, getGameBySlug, getGameList } from "@/common/games"

function uniqueUrls(items) {
  var seen = {}
  return items.filter(function(item) {
    if (!item || seen[item]) {
      return false
    }
    seen[item] = true
    return true
  })
}

export default {
  name: "Game",
  components: { GameLeft, GameRight },
  data() {
    return {
      progress: 1,
      btnText: "Loading assets...",
      areaCss: "overflow-y:hidden",
      frameCss: "",
      item: {},
      gameUrl: "",
      preloadUrl: "",
      start: false,
      isFullscreen: false,
      frameReady: false,
      preloadReady: false,
      preloadToken: 0,
      frameMonitorTimer: null,
      frameReadyFallbackTimer: null,
      frameOrientation: "portrait"
    }
  },
  computed: {
    relatedGames() {
      var games = getGameList()
      return games.filter(game => game.id !== this.item.id).slice(0, 4)
    },
    frameStatusText() {
      if (this.preloadReady) {
        return "Core files are ready. Finalizing the live play session."
      }

      return "Preparing the game canvas and loading the first scene."
    }
  },
  created() {
    this.getGameInfo()
    this.setupFrame()
    this.startPreload()
  },
  mounted() {
    window.addEventListener("resize", this.setupFrame)
    window.addEventListener("message", this.onBridgeMessage)
    document.addEventListener("fullscreenchange", this.syncFullscreenState)
    document.addEventListener("webkitfullscreenchange", this.syncFullscreenState)
    document.addEventListener("mozfullscreenchange", this.syncFullscreenState)
    document.addEventListener("MSFullscreenChange", this.syncFullscreenState)
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.setupFrame)
    window.removeEventListener("message", this.onBridgeMessage)
    document.removeEventListener("fullscreenchange", this.syncFullscreenState)
    document.removeEventListener("webkitfullscreenchange", this.syncFullscreenState)
    document.removeEventListener("mozfullscreenchange", this.syncFullscreenState)
    document.removeEventListener("MSFullscreenChange", this.syncFullscreenState)
    this.stopFrameMonitoring()
  },
  methods: {
    isRemoteNativeGame() {
      if (!this.item || this.item.integrationMode !== "native") {
        return false
      }

      return /^https?:\/\//i.test(this.preloadUrl || "")
    },
    stopFrameMonitoring() {
      if (this.frameMonitorTimer) {
        clearInterval(this.frameMonitorTimer)
        this.frameMonitorTimer = null
      }

      if (this.frameReadyFallbackTimer) {
        clearTimeout(this.frameReadyFallbackTimer)
        this.frameReadyFallbackTimer = null
      }
    },
    setupFrame() {
      var isMobile = this.global.common.isMobile()
      var aspectRatio =
        this.frameOrientation === "landscape" ? LANDSCAPE_RATIO : PORTRAIT_RATIO
      var maxWidth = isMobile
        ? Math.max(window.innerWidth - 32, 280)
        : Math.max(window.innerWidth - 620, 320)
      var maxHeight = isMobile
        ? Math.max(window.innerHeight - 240, 260)
        : Math.max(window.innerHeight - 180, 520)
      var frameWidth = maxWidth
      var frameHeight = frameWidth / aspectRatio

      if (frameHeight > maxHeight) {
        frameHeight = maxHeight
        frameWidth = frameHeight * aspectRatio
      }

      this.areaCss = ""
      this.frameCss =
        "width:" +
        Math.round(frameWidth) +
        "px;height:" +
        Math.round(frameHeight) +
        "px;"
    },
    syncFrameOrientation(htmlText) {
      var match = htmlText && htmlText.match(/orientation\s*:\s*"([^"]+)"/i)
      if (this.item.orientation) {
        this.frameOrientation = this.item.orientation
      } else {
        this.frameOrientation = match && match[1] === "horizontal" ? "landscape" : "portrait"
      }
      this.setupFrame()
    },
    applyGameConfig() {
      this.gameUrl = this.item.hostedPath || ""
      this.preloadUrl = this.item.gamePath || this.item.path || ""
      this.frameOrientation = this.item.orientation || "portrait"
    },
    setProgress(value, text) {
      this.progress = Math.max(this.progress, Math.min(value, 100))
      if (text) {
        this.btnText = text
      }
    },
    extractAssetUrls(htmlText) {
      var assetUrls = []
      var patterns = [
        /<(?:script|img)[^>]+src=["']([^"'#?]+(?:\?[^"']*)?)["']/gi,
        /<link[^>]+href=["']([^"'#?]+(?:\?[^"']*)?)["']/gi
      ]

      patterns.forEach(function(pattern) {
        var match = pattern.exec(htmlText)
        while (match) {
          assetUrls.push(match[1])
          match = pattern.exec(htmlText)
        }
      })

      return uniqueUrls(assetUrls)
        .map(function(rawUrl) {
          try {
            return new URL(rawUrl, window.location.origin + this.preloadUrl).toString()
          } catch (error) {
            return null
          }
        }, this)
        .filter(function(url) {
          return url && url.indexOf(window.location.origin) === 0
        })
    },
    fetchWithProgress(url, onProgress) {
      return fetch(url, { credentials: "same-origin" }).then(function(response) {
        if (!response.ok) {
          throw new Error("Failed to fetch game shell")
        }

        var total = Number(response.headers.get("content-length")) || 0
        if (!response.body || !window.ReadableStream) {
          return response.text().then(function(text) {
            onProgress(total || 1, total || 1)
            return text
          })
        }

        var reader = response.body.getReader()
        var decoder = new TextDecoder()
        var received = 0
        var chunks = []

        return new Promise(function(resolve, reject) {
          function pump() {
            reader.read().then(function(result) {
              if (result.done) {
                onProgress(total || received || 1, total || received || 1)
                resolve(chunks.join(""))
                return
              }

              received += result.value.length
              chunks.push(decoder.decode(result.value, { stream: true }))
              onProgress(received, total)
              pump()
            }).catch(reject)
          }

          pump()
        })
      })
    },
    preloadAssets(urls, token) {
      var self = this
      var total = urls.length
      if (!total) {
        this.setProgress(90, "Game shell ready")
        return Promise.resolve()
      }

      var completed = 0
      this.setProgress(40, "Preloading launch assets...")

      return Promise.all(urls.map(function(url) {
        return fetch(url, { credentials: "same-origin" })
          .catch(function() {
            return null
          })
          .then(function() {
            if (token !== self.preloadToken) {
              return
            }
            completed += 1
            self.setProgress(40 + (completed / total) * 50, "Preloading launch assets...")
          })
      })).then(function() {
        if (token !== self.preloadToken) {
          return
        }
        self.setProgress(90, "Game shell ready")
      })
    },
    startPreload() {
      var self = this
      var token = Date.now()
      this.preloadToken = token
      this.progress = 2
      this.preloadReady = false
      this.frameReady = false
      this.btnText = "Fetching game shell..."

      if (!this.preloadUrl) {
        this.btnText = "Game unavailable"
        return
      }

      if (this.isRemoteNativeGame()) {
        this.preloadReady = true
        this.setProgress(90, "Ready to launch")
        return
      }

      this.fetchWithProgress(this.preloadUrl, function(received, total) {
        if (token !== self.preloadToken) {
          return
        }

        if (total > 0) {
          self.setProgress(5 + (received / total) * 35, "Fetching game shell...")
        } else {
          self.setProgress(25, "Fetching game shell...")
        }
      }).then(function(htmlText) {
        if (token !== self.preloadToken) {
          return
        }

        self.syncFrameOrientation(htmlText)
        var assetUrls = self.extractAssetUrls(htmlText)
        return self.preloadAssets(assetUrls, token)
      }).then(function() {
        if (token !== self.preloadToken) {
          return
        }

        self.preloadReady = true
        self.setProgress(90, "Ready to launch")
      }).catch(function() {
        if (token !== self.preloadToken) {
          return
        }

        self.preloadReady = true
        self.setProgress(90, "Ready to launch")
      })
    },
    onStart() {
      if (!this.preloadReady) {
        return
      }
      this.start = true
      this.frameReady = false
      this.setProgress(95, "Opening play session...")
      this.$nextTick(() => {
        this.startFrameMonitoring()
      })
    },
    startFrameMonitoring() {
      var self = this
      this.stopFrameMonitoring()
      this.frameMonitorTimer = setInterval(function() {
        var frame = self.$refs.gameFrame
        if (!frame) {
          return
        }

        try {
          var doc = frame.contentDocument
          var body = doc && doc.body
          var hasCanvas = body && body.querySelector("canvas")
          var hasGameContainer = body && (
            body.querySelector("#Cocos2dGameContainer") ||
            body.querySelector("#gameCanvas")
          )

          if (hasCanvas || hasGameContainer) {
            self.markFrameReady()
          }
        } catch (error) {
          // Same-origin game pages are expected here. Ignore transient access errors while the frame navigates.
        }
      }, 250)
    },
    markFrameReady() {
      if (this.frameReady) {
        return
      }

      this.stopFrameMonitoring()
      this.frameReady = true
      this.setProgress(100, "Game live")
    },
    onFrameLoad() {
      if (this.isRemoteNativeGame()) {
        if (this.$refs.gameFrame && this.$refs.gameFrame.focus) {
          this.$refs.gameFrame.focus()
        }
        this.markFrameReady()
        return
      }

      if (!this.frameReady) {
        this.setProgress(97, "Opening play session...")
        if (this.frameReadyFallbackTimer) {
          clearTimeout(this.frameReadyFallbackTimer)
        }
        this.frameReadyFallbackTimer = setTimeout(() => {
          if (!this.frameReady && this.start) {
            this.markFrameReady()
          }
        }, 1200)
      }
    },
    onBridgeMessage(event) {
      var data = event && event.data
      if (!data || data.source !== "free-game-bridge") {
        return
      }

      if (data.type === "ready") {
        if (data.payload && data.payload.orientation) {
          this.frameOrientation = data.payload.orientation
          this.setupFrame()
        }
        this.markFrameReady()
        return
      }
    },
    enterFullscreen() {
      var target = this.$refs.gameFrame || document.documentElement

      if (!target) {
        return
      }

      if (target.requestFullscreen) {
        target.requestFullscreen()
      } else if (target.msRequestFullscreen) {
        target.msRequestFullscreen()
      } else if (target.mozRequestFullScreen) {
        target.mozRequestFullScreen()
      } else if (target.webkitRequestFullscreen) {
        target.webkitRequestFullscreen()
      } else if (target.webkitRequestFullScreen) {
        target.webkitRequestFullScreen()
      }
    },
    exitFullscreen() {
      if (document.exitFullscreen) {
        document.exitFullscreen()
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen()
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen()
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen()
      } else if (document.webkitCancelFullScreen) {
        document.webkitCancelFullScreen()
      }
    },
    toggleFullscreen() {
      if (this.isFullscreen) {
        this.exitFullscreen()
      } else {
        this.enterFullscreen()
      }
    },
    syncFullscreenState() {
      this.isFullscreen = Boolean(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
      )
    },
    cleanDesc(desc) {
      return cleanDesc(desc)
    },
    resetStageState() {
      this.stopFrameMonitoring()
      this.preloadToken = Date.now()
      this.start = false
      this.frameReady = false
      this.preloadReady = false
      this.isFullscreen = false
      this.progress = 1
      this.btnText = "Loading assets..."
      this.frameOrientation = this.item.orientation || "portrait"
      this.applyGameConfig()
      this.setupFrame()
      this.startPreload()
    },
    getGameInfo() {
      var slug = this.$route.params.slug
      var legacyId = this.$route.query.id
      this.item = getGameBySlug(slug) || getGameById(legacyId) || {}
      this.applyGameConfig()
    }
  },
  watch: {
    "$route.params.slug": function() {
      this.getGameInfo()
      this.resetStageState()
    },
    "$route.query.id": function() {
      this.getGameInfo()
      this.resetStageState()
    }
  }
}
</script>

<style scoped>
.game-page {
  min-height: 100vh;
  color: #f8fafc;
  background:
    radial-gradient(circle at top, rgba(255, 115, 0, 0.12), transparent 28%),
    radial-gradient(circle at 80% 15%, rgba(97, 218, 251, 0.12), transparent 22%),
    linear-gradient(180deg, #040506 0%, #090b0f 45%, #040506 100%);
}

.game-shell {
  width: min(1320px, calc(100% - 32px));
  margin: 0 auto;
  padding: 24px 0 32px;
}

.game-overview,
.game-stage {
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 30px;
  background: rgba(9, 11, 15, 0.88);
  box-shadow: 0 28px 60px rgba(0, 0, 0, 0.38);
  backdrop-filter: blur(18px);
  animation: stageReveal 0.75s ease both;
}

.game-overview {
  display: grid;
  grid-template-columns: minmax(0, 1.08fr) minmax(320px, 0.92fr);
  gap: 24px;
  padding: 26px;
  position: relative;
  overflow: hidden;
}

.game-overview::before {
  content: "";
  position: absolute;
  top: -140px;
  right: -140px;
  width: 320px;
  height: 320px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.1), transparent 60%);
  opacity: 0.35;
  pointer-events: none;
}

.overview-copy {
  position: relative;
  padding: 10px;
}

.overview-glow {
  position: absolute;
  top: 4%;
  right: 8%;
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 166, 122, 0.18), transparent 68%);
  pointer-events: none;
  animation: pulseAura 7s ease-in-out infinite;
}

.crumbs,
.stage-kicker,
.status-tag {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: rgba(226, 232, 240, 0.66);
  font: 600 12px/1.2 "Courier New", monospace;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.crumbs a {
  color: #f8fafc;
}

.status-tag {
  margin: 20px 0 16px;
  min-height: 28px;
  padding: 0 14px;
  border-radius: 999px;
  background: rgba(255, 124, 92, 0.12);
  border: 1px solid rgba(255, 124, 92, 0.26);
  color: #ff9a7d;
}

.overview-copy h1,
.stage-topbar h2 {
  margin-bottom: 16px;
  font: 700 clamp(34px, 4.8vw, 64px)/0.95 Georgia, serif;
  letter-spacing: -0.04em;
}

.overview-desc {
  max-width: 44rem;
  color: rgba(226, 232, 240, 0.8);
  font-size: 16px;
  line-height: 1.8;
}

.overview-meta {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
  margin: 28px 0;
}

.overview-meta div {
  position: relative;
  padding: 18px;
  border-radius: 22px;
  border: 1px solid rgba(255, 255, 255, 0.07);
  background: rgba(255, 255, 255, 0.03);
}

.meta-icon,
.button-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.meta-icon {
  width: 34px;
  height: 34px;
  margin-bottom: 12px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.meta-icon svg,
.button-icon svg {
  width: 18px;
  height: 18px;
  stroke: currentColor;
  fill: none;
  stroke-width: 1.7;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.overview-meta strong {
  display: block;
  margin-bottom: 8px;
  font: 700 24px/1 Georgia, serif;
}

.overview-meta span {
  color: rgba(226, 232, 240, 0.72);
  font-size: 13px;
}

.play-launch {
  max-width: 540px;
}

.launch-button {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 64px;
  padding: 0 22px;
  border: 0;
  border-radius: 22px;
  background: linear-gradient(135deg, #fafafa, #7a818c);
  color: #060709;
  font: 700 15px/1 "Trebuchet MS", "Arial Black", sans-serif;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  box-shadow: 0 16px 36px rgba(255, 255, 255, 0.16);
}

.launch-button:disabled {
  cursor: progress;
  opacity: 0.84;
}

.launch-button em {
  font: 600 11px/1.2 "Courier New", monospace;
}

.launch-progress {
  overflow: hidden;
  height: 8px;
  margin-top: 14px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
}

.launch-bar {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #ff8f70, #dcdcdc);
}

.store-links {
  display: flex;
  gap: 14px;
  margin-top: 24px;
  flex-wrap: wrap;
}

.store-links img {
  height: 42px;
}

.overview-art {
  position: relative;
  overflow: hidden;
  min-height: 460px;
  border-radius: 24px;
}

.overview-art::after {
  content: "";
  position: absolute;
  inset: 0;
  background:
    linear-gradient(180deg, rgba(0, 0, 0, 0.08), rgba(0, 0, 0, 0.75)),
    linear-gradient(135deg, rgba(255, 255, 255, 0.08), transparent 40%);
}

.overview-art img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.overview-art-overlay {
  position: absolute;
  right: 22px;
  bottom: 22px;
  z-index: 1;
  max-width: 70%;
  padding: 16px 18px;
  border-radius: 20px;
  background: rgba(7, 9, 11, 0.72);
  border: 1px solid rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(14px);
}

.overview-art-overlay span {
  display: block;
  margin-bottom: 8px;
  color: rgba(226, 232, 240, 0.72);
  font: 600 11px/1 "Courier New", monospace;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.overview-art-overlay strong {
  font: 700 24px/1.1 Georgia, serif;
}

.game-stage {
  padding: 20px;
  position: relative;
}

.stage-topbar {
  display: flex;
  align-items: end;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 20px;
  padding: 8px 6px 0;
}

.stage-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.stage-fullscreen,
.stage-back {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 48px;
  padding: 0 18px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  color: #f8fafc;
  background: rgba(255, 255, 255, 0.03);
  font: 600 13px/1 "Trebuchet MS", "Segoe UI", sans-serif;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.button-icon {
  width: 28px;
  height: 28px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.05);
}

.stage-layout {
  display: grid;
  grid-template-columns: 260px minmax(0, 1fr) 280px;
  gap: 18px;
  align-items: start;
}

.stage-frame-shell {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 76vh;
  border-radius: 28px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background:
    radial-gradient(circle at top, rgba(255, 255, 255, 0.08), transparent 32%),
    #020304;
  overflow: auto;
}

.frame-aura {
  position: absolute;
  inset: auto auto 8% 50%;
  width: 280px;
  height: 280px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(120, 225, 255, 0.12), transparent 68%);
  transform: translateX(-50%);
  pointer-events: none;
  filter: blur(10px);
}

.frame-loading {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: grid;
  place-items: center;
  padding: 28px;
  background:
    linear-gradient(180deg, rgba(3, 4, 6, 0.52), rgba(3, 4, 6, 0.9)),
    radial-gradient(circle at top, rgba(255, 255, 255, 0.06), transparent 32%);
}

.frame-loading-art {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0.28;
  filter: blur(14px);
  transform: scale(1.06);
}

.frame-loading-copy {
  position: relative;
  z-index: 1;
  display: grid;
  gap: 12px;
  width: min(440px, 100%);
  padding: 24px;
  border-radius: 24px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(8, 10, 14, 0.8);
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.34);
  backdrop-filter: blur(16px);
  text-align: center;
}

.frame-loading-kicker {
  color: rgba(226, 232, 240, 0.72);
  font: 600 11px/1.2 "Courier New", monospace;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.frame-loading-copy strong {
  font: 700 clamp(28px, 4vw, 40px)/1 Georgia, serif;
}

.frame-loading-copy p {
  color: rgba(226, 232, 240, 0.8);
  font-size: 15px;
  line-height: 1.7;
}

.frame-loading-progress {
  overflow: hidden;
  height: 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.34);
}

.frame-loading-progress-bar {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #ff8f70, #ffd66b 52%, #92f4ff);
  box-shadow: 0 0 24px rgba(255, 214, 107, 0.28);
  transition: width 0.24s ease;
}

.frame-loading-progress-text {
  color: rgba(248, 250, 252, 0.86);
  font: 600 12px/1 "Courier New", monospace;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

#gameFrame {
  position: relative;
  z-index: 1;
  max-width: 100%;
  border-radius: 20px;
  background: #000;
  box-shadow: 0 24px 44px rgba(0, 0, 0, 0.42);
  opacity: 0;
  transition: opacity 0.28s ease;
}

#gameFrame.is-ready {
  opacity: 1;
}

@keyframes stageReveal {
  from {
    opacity: 0;
    transform: translateY(16px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes pulseAura {
  0%,
  100% {
    transform: scale(1);
    opacity: 0.65;
  }
  50% {
    transform: scale(1.12);
    opacity: 0.95;
  }
}

@media (max-width: 1180px) {
  .stage-layout {
    grid-template-columns: 220px minmax(0, 1fr);
  }
}

@media (max-width: 960px) {
  .game-shell {
    width: min(100%, calc(100% - 20px));
    padding-top: 14px;
  }

  .game-overview {
    grid-template-columns: 1fr;
  }

  .overview-art {
    min-height: 320px;
  }

  .overview-glow,
  .frame-aura {
    display: none;
  }

  .overview-meta,
  .stage-layout {
    grid-template-columns: 1fr;
  }

  .stage-layout > :nth-child(2) {
    order: -1;
  }

  .stage-topbar {
    flex-direction: column;
    align-items: flex-start;
  }

  .stage-actions {
    width: 100%;
  }

  .stage-frame-shell {
    min-height: 60vh;
  }

  .stage-fullscreen,
  .launch-button,
  .stage-back {
    width: 100%;
  }
}

@media (max-width: 560px) {
  .game-overview,
  .game-stage {
    padding: 14px;
    border-radius: 24px;
  }

  .overview-copy h1,
  .stage-topbar h2 {
    font-size: 36px;
  }

  .overview-copy {
    padding: 4px;
  }

  .overview-meta {
    gap: 10px;
    margin: 20px 0;
  }

  .overview-meta div {
    padding: 14px;
    border-radius: 18px;
  }

  .overview-desc {
    font-size: 15px;
    line-height: 1.7;
  }

  .play-launch {
    max-width: none;
  }

  .launch-button {
    min-height: 58px;
    padding: 0 18px;
    border-radius: 18px;
    font-size: 14px;
  }

  .launch-button em {
    font-size: 10px;
  }

  .overview-art {
    min-height: 240px;
    border-radius: 18px;
  }

  .overview-art-overlay {
    right: 14px;
    bottom: 14px;
    max-width: calc(100% - 28px);
    padding: 14px;
    border-radius: 16px;
  }

  .game-stage {
    padding: 12px;
  }

  .stage-topbar {
    gap: 12px;
    margin-bottom: 14px;
    padding: 4px 2px 0;
  }

  .stage-frame-shell {
    min-height: 52vh;
    padding: 8px;
    border-radius: 18px;
    overflow: hidden;
  }

  #gameFrame {
    width: 100% !important;
    height: 52vh !important;
    border-radius: 14px;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/Game.vue